import {
  ChangeDetectionStrategy,
  Component,
  EnvironmentInjector,
  PLATFORM_ID,
  afterNextRender,
  inject,
  effect,
  runInInjectionContext,
} from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import {
  NavigationEnd,
  NavigationStart,
  Router,
  RouterModule,
} from '@angular/router';
import { FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { WebAuthService } from './core/auth/auth.service';
import { AsyncPipe, CommonModule } from '@angular/common';
import { setSentryConsent } from './core/sentry';
import { CookieConsentService } from './core/privacy/cookie-consent.service';
import { CookieNoticeComponent } from './shared/components/cookie-notice/cookie-notice.component';
import { AppFooterComponent } from './shared/components/app-footer/app-footer.component';
import { GtagLoaderService } from './shared/analytics/gtag-loader.service';
import { PageViewTrackerService } from './shared/analytics/page-view-tracker.service';
import { NavigationErrorRecoveryService } from './core/routing/navigation-error-recovery.service';
import { isPrivateTicketPage } from './core/privacy/recovery-link';

@Component({
  selector: 'app-root',
  imports: [
    RouterModule,
    ReactiveFormsModule,
    AsyncPipe,
    CommonModule,
    AppFooterComponent,
    CookieNoticeComponent,
  ],
  templateUrl: './app.html',
  styleUrl: './app.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class App {
  protected readonly search = new FormControl('', { nonNullable: true });
  protected readonly searchForm = new FormGroup({ query: this.search });
  private readonly router = inject(Router);

  protected submitSearch(): void {
    this.closeMobileMenu();
    void this.router.navigate(['/eventos'], {
      queryParams: { q: this.search.value.trim() || null },
    });
  }

  private readonly consent = inject(CookieConsentService);
  protected title = 'AccessHub Web';
  protected readonly auth = inject(WebAuthService);
  protected mobileMenuOpen = false;
  private readonly environmentInjector = inject(EnvironmentInjector);
  private readonly platformId = inject(PLATFORM_ID);
  private readonly gtagLoader = inject(GtagLoaderService);
  private readonly pageViewTracker = inject(PageViewTrackerService);
  private readonly navigationErrorRecovery = inject(
    NavigationErrorRecoveryService
  );

  constructor() {
    if (!isPlatformBrowser(this.platformId)) {
      return;
    }

    effect(() => {
      const allowed =
        this.consent.allows('analytics') &&
        !isPrivateTicketPage(window.location.pathname);
      setSentryConsent(allowed);
      if (allowed) this.loadAnalytics();
      else this.gtagLoader.disable();
    });
    this.router.events.subscribe((event) => {
      if (event instanceof NavigationStart && isPrivateTicketPage(event.url)) {
        this.gtagLoader.disable();
        setSentryConsent(false);
        // Unload installed third-party SDKs before entering the sensitive flow.
        if (
          document.querySelector(
            'script[data-ga-id], script[src*="pagead2.googlesyndication.com/pagead/js/adsbygoogle.js"]'
          )
        )
          window.location.assign(event.url);
      }
      if (
        event instanceof NavigationEnd &&
        !isPrivateTicketPage(event.urlAfterRedirects) &&
        this.consent.allows('analytics')
      ) {
        setSentryConsent(true);
        this.loadAnalytics();
      }
    });
    afterNextRender(() => {
      this.navigationErrorRecovery.init();
      this.consent.init();
    });
  }

  private loadAnalytics(): void {
    this.gtagLoader.init();
    this.pageViewTracker.init();
  }

  goToAdmin(path: string): void {
    this.closeMobileMenu();
    void this.openAdmin(path);
  }

  private async openAdmin(path: string): Promise<void> {
    const { WebAdminHandoffService } = await import(
      './core/auth/admin-handoff.service'
    );
    runInInjectionContext(this.environmentInjector, () => {
      inject(WebAdminHandoffService).openAdmin(path).subscribe();
    });
  }

  scrollToTop(): void {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }

  toggleMobileMenu(): void {
    this.mobileMenuOpen = !this.mobileMenuOpen;
  }

  closeMobileMenu(): void {
    this.mobileMenuOpen = false;
  }
}

// CICD 2
